// Admin report of driver deductions: every charge, each deduction/payment
// taken against it, and what's still owed -- filtered by driver and date
// range, deactivated drivers included, exportable to CSV. Reads the
// deduction ledger (src/lib/driverDeductions.ts); nothing here edits history.
// Reversing a deduction, correcting a balance or cancelling a charge each
// write a new, reasoned ledger row instead of overwriting anything.
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import { canEditTab } from "@/lib/permissions";
import { Download, RefreshCcw } from "lucide-react";
import {
  adjustDeductionBalance,
  cancelDeduction,
  deductionReportToCsv,
  loadDeductionReport,
  money,
  num,
  reverseDeduction,
  type DeductionReport,
  type LedgerRow,
  type ReportCharge,
} from "@/lib/driverDeductions";

type DriverOption = { driver_name: string; is_active: boolean | null };

const isoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const TYPE_LABEL: Record<string, string> = {
  charge: "Charge",
  deduction: "Deduction",
  reversal: "Reversal",
  adjustment: "Adjustment",
  cancellation: "Cancelled",
};

const STATUS_STYLE: Record<string, string> = {
  active: "border-green-500/50 text-green-500",
  adjusted: "border-yellow-500/50 text-yellow-500",
  paid: "border-blue-400/50 text-blue-400",
  cancelled: "border-red-400/50 text-red-400",
  removed: "border-gray-500/50 text-gray-400",
};

type PendingAction =
  | { kind: "reverse"; row: LedgerRow; charge: ReportCharge }
  | { kind: "adjust"; charge: ReportCharge }
  | { kind: "cancel"; charge: ReportCharge };

const DeductionsReport: React.FC = () => {
  const { effectivePermissions } = useAppContext();
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;
  const canEdit = canEditTab(effectivePermissions, "payout") || canEditTab(effectivePermissions, "summary");

  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return isoDate(d);
  });
  const [to, setTo] = useState(() => isoDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<DeductionReport | null>(null);

  const [pending, setPending] = useState<PendingAction | null>(null);
  const [note, setNote] = useState("");
  const [newBalance, setNewBalance] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    (async () => {
      // Deactivated drivers stay in the list: their charges and history are
      // exactly what this report is for. Names that only exist in the ledger
      // (a driver record since deleted) are added too.
      const [{ data: driverRows, error }, { data: ledgerNames }] = await Promise.all([
        supabase.from("drivers").select("driver_name, is_active").order("driver_name", { ascending: true }),
        supabase.from("driver_deduction_transactions").select("driver_name"),
      ]);
      if (error) {
        toast({ title: "Error loading drivers", description: error.message, variant: "destructive" });
        return;
      }
      const known = new Map<string, DriverOption>();
      ((driverRows as DriverOption[]) || []).forEach((d) => known.set(d.driver_name, d));
      ((ledgerNames as { driver_name: string }[]) || []).forEach((r) => {
        if (!known.has(r.driver_name)) known.set(r.driver_name, { driver_name: r.driver_name, is_active: false });
      });
      setDrivers([...known.values()].sort((a, b) => a.driver_name.localeCompare(b.driver_name)));
    })();
  }, []);

  const visibleDrivers = useMemo(() => {
    let list = drivers;
    if (scopedDriverNames) list = list.filter((d) => scopedDriverNames.includes(d.driver_name));
    if (!includeInactive) list = list.filter((d) => d.is_active !== false);
    const q = search.trim().toLowerCase();
    return q ? list.filter((d) => d.driver_name.toLowerCase().includes(q)) : list;
  }, [drivers, scopedDriverNames, includeInactive, search]);

  const toggleDriver = (name: string) =>
    setSelected((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  const run = async () => {
    setLoading(true);
    try {
      const names = selected.length > 0 ? selected : scopedDriverNames ?? [];
      setReport(await loadDeductionReport({ driverNames: names, from, to }));
    } catch (e) {
      toast({
        title: "Couldn't load the report",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!report) return;
    const blob = new Blob([deductionReportToCsv(report, from, to)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `driver-deductions_${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openAction = (action: PendingAction) => {
    setPending(action);
    setNote("");
    setNewBalance(action.kind === "adjust" ? String(action.charge.remaining ?? "") : "");
  };

  const submitAction = async () => {
    if (!pending) return;
    if (!note.trim()) {
      toast({ title: "A reason is required", description: "It's saved with the entry so the history explains itself.", variant: "destructive" });
      return;
    }
    setWorking(true);
    try {
      if (pending.kind === "reverse") {
        await reverseDeduction(pending.row.id, note.trim());
      } else if (pending.kind === "cancel") {
        if (!pending.charge.adjustmentId) throw new Error("This charge no longer exists.");
        await cancelDeduction(pending.charge.adjustmentId, note.trim());
      } else {
        const value = Number(newBalance);
        if (!pending.charge.adjustmentId || newBalance === "" || !Number.isFinite(value) || value < 0) {
          throw new Error("Enter a balance of $0 or more.");
        }
        await adjustDeductionBalance(pending.charge.adjustmentId, value, note.trim());
      }
      toast({ title: "Saved", description: "A new entry was added to the history." });
      setPending(null);
      await run();
    } catch (e) {
      toast({
        title: "Couldn't save that",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setWorking(false);
    }
  };

  const actionTitle =
    pending?.kind === "reverse"
      ? `Reverse ${money(num(pending.row.amount))} deduction`
      : pending?.kind === "cancel"
      ? "Cancel this charge"
      : "Correct the balance";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Driver Deductions Report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Every charge and the deductions taken against it, with the balance still owed. Deductions are recorded
            when a week's bank file is exported. Nothing is ever overwritten: reversals, corrections and cancellations
            are added as new entries, each with a reason.
          </p>

          <div className="flex flex-wrap gap-3 items-center">
            <Input placeholder="Search drivers..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={includeInactive} onCheckedChange={(v) => setIncludeInactive(Boolean(v))} />
              Include deactivated drivers
            </label>
            <Button size="sm" onClick={() => setSelected(visibleDrivers.map((d) => d.driver_name))}>Select All</Button>
            <Button size="sm" variant="outline" onClick={() => setSelected([])}>Clear</Button>
            <span className="text-xs text-muted-foreground">{selected.length === 0 ? "No one ticked = all drivers" : `${selected.length} ticked`}</span>
          </div>

          <div className="max-h-52 overflow-y-auto border rounded p-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
            {visibleDrivers.map((d) => (
              <label key={d.driver_name} className="flex items-center gap-2 text-sm">
                <Checkbox checked={selected.includes(d.driver_name)} onCheckedChange={() => toggleDriver(d.driver_name)} />
                <span>{d.driver_name}</span>
                {d.is_active === false && <Badge variant="outline" className="text-[10px] px-1 py-0 text-muted-foreground">Inactive</Badge>}
              </label>
            ))}
            {visibleDrivers.length === 0 && <div className="text-sm text-muted-foreground p-2">No drivers match.</div>}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
            </div>
            <Button onClick={run} disabled={loading}>
              <RefreshCcw className="mr-2 h-4 w-4" />
              {loading ? "Loading..." : "Run Report"}
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!report || report.charges.length === 0}>
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {report && report.charges.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-6">No charges or deductions in that period.</p>
      )}

      {report && report.totals.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Totals for the period</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-1.5 pr-3">Driver</th>
                    <th className="py-1.5 pr-3 text-right">New charges</th>
                    <th className="py-1.5 pr-3 text-right">Deducted</th>
                    <th className="py-1.5 text-right">Still owed</th>
                  </tr>
                </thead>
                <tbody>
                  {report.totals.map((t) => (
                    <tr key={t.driverName} className="border-b border-border/40">
                      <td className="py-1.5 pr-3">{t.driverName}</td>
                      <td className="py-1.5 pr-3 text-right">{money(t.charged)}</td>
                      <td className="py-1.5 pr-3 text-right">{money(t.deducted)}</td>
                      <td className="py-1.5 text-right font-semibold">{money(t.remaining)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {report?.charges.map((c) => (
        <Card key={c.key}>
          <CardContent className="pt-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{c.driverName}</span>
                  <span className="text-muted-foreground">·</span>
                  <span>{c.description}</span>
                  <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[c.status] ?? ""}`}>
                    {c.status === "removed" ? "Charge removed" : c.status}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                  {c.chargeDate && <span>Charged {c.chargeDate}</span>}
                  {c.originalAmount !== null && <span>Original {money(c.originalAmount)}</span>}
                  <span>Deducted in period {money(c.deductedInPeriod)}</span>
                  <span>Paid to date {money(c.paidToDate)}</span>
                  {c.remaining !== null && <span className="text-foreground font-medium">Remaining {money(c.remaining)}</span>}
                </div>
              </div>
              {canEdit && c.adjustmentId && c.status !== "cancelled" && (
                <div className="flex gap-2">
                  {c.mode === "reducing_balance" && (
                    <Button size="sm" variant="outline" onClick={() => openAction({ kind: "adjust", charge: c })}>Correct balance</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openAction({ kind: "cancel", charge: c })}>Cancel charge</Button>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-1 pr-3">Date</th>
                    <th className="py-1 pr-3">Entry</th>
                    <th className="py-1 pr-3">Pay week</th>
                    <th className="py-1 pr-3 text-right">Amount</th>
                    <th className="py-1 pr-3 text-right">Balance after</th>
                    <th className="py-1 pr-3">Note</th>
                    <th className="py-1" />
                  </tr>
                </thead>
                <tbody>
                  {c.rows.map((r) => (
                    <tr key={r.id} className={`border-b border-border/40 ${r.reversed_at ? "opacity-60" : ""}`}>
                      <td className="py-1 pr-3 whitespace-nowrap">{r.txn_date}</td>
                      <td className="py-1 pr-3">
                        {TYPE_LABEL[r.txn_type] ?? r.txn_type}
                        {r.reversed_at && <span className="text-xs text-muted-foreground"> (reversed)</span>}
                      </td>
                      <td className="py-1 pr-3 whitespace-nowrap text-xs text-muted-foreground">
                        {r.week_start ? `${r.week_start} → ${r.week_end ?? ""}` : "—"}
                      </td>
                      <td className="py-1 pr-3 text-right">
                        {r.txn_type === "deduction" || r.txn_type === "cancellation" ? "-" : r.txn_type === "adjustment" && num(r.balance_delta) < 0 ? "-" : ""}
                        {money(num(r.amount))}
                      </td>
                      <td className="py-1 pr-3 text-right">{r.balance_after === null ? "—" : money(num(r.balance_after))}</td>
                      <td className="py-1 pr-3 text-xs text-muted-foreground">{r.note ?? ""}</td>
                      <td className="py-1 text-right">
                        {canEdit && r.txn_type === "deduction" && !r.reversed_at && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => openAction({ kind: "reverse", row: r, charge: c })}>
                            Reverse
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      <Dialog open={!!pending} onOpenChange={(open) => !open && !working && setPending(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{actionTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {pending?.kind === "reverse" && (
              <p className="text-sm text-muted-foreground">
                The {money(num(pending.row.amount))} goes back onto {pending.charge.driverName}'s balance and a reversal is
                recorded. The original deduction stays in the history. If this already went out in a bank file, that payment
                isn't undone here.
              </p>
            )}
            {pending?.kind === "cancel" && (
              <p className="text-sm text-muted-foreground">
                Stops "{pending.charge.description}" being taken from {pending.charge.driverName}.
                {pending.charge.remaining ? ` The ${money(pending.charge.remaining)} still owed is written off and recorded.` : ""} The history stays.
              </p>
            )}
            {pending?.kind === "adjust" && (
              <div>
                <Label>Balance now owed ($)</Label>
                <Input type="number" min="0" step="0.01" value={newBalance} onChange={(e) => setNewBalance(e.target.value)} />
              </div>
            )}
            <div>
              <Label>Reason (required)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="e.g. Driver paid $200 cash on 3 Sept" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} disabled={working}>Back</Button>
            <Button onClick={submitAction} disabled={working}>{working ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeductionsReport;
